// SEO Service - Comprehensive SEO utilities and functions
import pagesSeoData from "../data/pages-seo.json";

export interface MetaTags {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogImage: string;
  ogType: "website" | "article" | "product";
  noIndex: boolean;
  structuredData?: any[];
}

export interface StructuredData {
  "@context": string;
  "@type": string;
  [key: string]: any;
}

export interface PageSEO {
  metaTags: MetaTags;
  structuredData: StructuredData[];
}

// Default SEO configuration
export const DEFAULT_SEO: MetaTags = {
  title: "Web Design & Development Agency | Custom Websites | MoizWP",
  description:
    "Transform your business with stunning websites and powerful digital solutions. Expert web design, development, and SEO services. Get your free quote today!",
  keywords:
    "web design agency, website development, custom websites, web design services, SEO services, e-commerce development",
  canonical: "https://moizwp.com",
  ogImage: "/og-image.jpg",
  ogType: "website",
  noIndex: false,
};

// Type definition for the JSON data
const PAGES_SEO: Record<string, Partial<MetaTags>> = pagesSeoData;

// SEO configurations for different page types (maintained for backward compatibility and internal labels)
export const PAGE_SEO_CONFIGS: Record<string, Partial<MetaTags>> = {
  ...PAGES_SEO,
  // Add any legacy mappings if needed
};

/**
 * Resolves metadata for a specific URL path
 * @param path The current window.location.pathname
 * @returns MetaTags for the path or DEFAULT_SEO if not found
 */
export const getMetadataForPath = (path: string): MetaTags => {
  // Try exact match
  let config = PAGES_SEO[path];

  // Try match without trailing slash
  if (!config && path.endsWith("/") && path.length > 1) {
    config = PAGES_SEO[path.slice(0, -1)];
  }

  // Handle blog posts specifically if path starts with /blog/
  if (!config && path.startsWith("/blog/") && path.length > 6) {
    // Blog post metadata is usually dynamic, so we return a modified default or partial
    // Try to derive a title from the slug for better initial metadata
    const slug = path.replace("/blog/", "").split("/")[0];
    const derivedTitle = slug
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    return {
      ...DEFAULT_SEO,
      title: `${derivedTitle} | MoizWP Blog`,
      ogType: "article",
    };
  }

  // Handle cases where '/' might be ''
  if (!config && (path === "" || path === "/")) {
    config = PAGES_SEO["/"];
  }

  return {
    ...DEFAULT_SEO,
    ...(config || {}),
    canonical: `https://moizwp.com${path === "/" ? "" : path.replace(/\/$/, "")}`,
    structuredData: config?.structuredData,
  };
};

// Generate meta tags for a specific page type or label
export const generateMetaTags = (
  pageType: string,
  customData?: Partial<MetaTags>,
): MetaTags => {
  const baseConfig = PAGE_SEO_CONFIGS[pageType] || PAGES_SEO[pageType] || {};
  const customConfig = customData || {};

  return {
    ...DEFAULT_SEO,
    ...baseConfig,
    ...customConfig,
    canonical:
      customConfig.canonical ||
      `https://moizwp.com${pageType === "home" || pageType === "/" ? "" : pageType.startsWith("/") ? pageType : "/" + pageType}`,
  };
};

// Generate structured data for different page types
export const generateStructuredData = (
  pageType: string,
  customData?: any,
): StructuredData[] => {
  const baseData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    url: `${DEFAULT_SEO.canonical}/${pageType === "home" ? "" : pageType}`,
    mainEntity: {
      "@type": "Organization",
      name: "MoizWP",
      url: DEFAULT_SEO.canonical,
      logo: `${DEFAULT_SEO.canonical}/logo.png`,
      description: "Professional web design and development agency",
    },
  };

  const pageSpecificData = getPageSpecificStructuredData(pageType, customData);

  return [baseData, ...pageSpecificData];
};

// Get page-specific structured data
const getPageSpecificStructuredData = (
  pageType: string,
  customData?: any,
): StructuredData[] => {
  switch (pageType) {
    case "services":
      return [
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Web Design and Development",
          provider: {
            "@type": "Organization",
            name: "MoizWP",
          },
          areaServed: "Worldwide",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Web Services",
            itemListElement: [
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Website Design",
                  description: "Custom website design services",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "Website Development",
                  description: "Custom website development services",
                },
              },
              {
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: "SEO Services",
                  description: "Search engine optimization services",
                },
              },
            ],
          },
        },
      ];

    case "portfolio":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Web Design Portfolio",
          description: "Collection of web design and development projects",
          itemListElement:
            customData?.projects?.map((project: any, index: number) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "CreativeWork",
                name: project.name,
                description: project.description,
                url: project.url,
                creator: {
                  "@type": "Organization",
                  name: "MoizWP",
                },
              },
            })) || [],
        },
      ];

    case "pricing":
      return [
        {
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          name: "Web Design Pricing",
          description:
            "Transparent pricing for web design and development services",
          priceCurrency: "USD",
          validFrom: new Date().toISOString().split("T")[0],
        },
      ];

    case "contact":
      return [
        {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact Us",
          description: "Get in touch for web design and development services",
          mainEntity: {
            "@type": "Organization",
            name: "MoizWP",
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer service",
              availableLanguage: "English",
            },
          },
        },
      ];

    default:
      return [];
  }
};

// Generate breadcrumb structured data
export const generateBreadcrumbData = (
  breadcrumbs: Array<{ name: string; url: string }>,
): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
};

// Generate FAQ structured data
export const generateFAQData = (
  faqs: Array<{ question: string; answer: string }>,
): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

// Generate review structured data
export const generateReviewData = (
  reviews: Array<{ author: string; rating: number; reviewBody: string }>,
): StructuredData => {
  return {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    itemReviewed: {
      "@type": "Organization",
      name: "MoizWP",
    },
    ratingValue:
      reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length,
    reviewCount: reviews.length,
    bestRating: 5,
    worstRating: 1,
  };
};

// SEO optimization utilities
export const optimizeTitle = (
  title: string,
  maxLength: number = 60,
): string => {
  if (title.length <= maxLength) return title;

  // Try to cut at word boundary
  const words = title.split(" ");
  let optimizedTitle = "";

  for (const word of words) {
    if ((optimizedTitle + " " + word).length <= maxLength) {
      optimizedTitle += (optimizedTitle ? " " : "") + word;
    } else {
      break;
    }
  }

  return optimizedTitle || title.substring(0, maxLength - 3) + "...";
};

export const optimizeDescription = (
  description: string,
  maxLength: number = 160,
): string => {
  if (description.length <= maxLength) return description;

  // Try to cut at sentence boundary
  const sentences = description.split(/[.!?]/);
  let optimizedDescription = "";

  for (const sentence of sentences) {
    if ((optimizedDescription + sentence + ".").length <= maxLength) {
      optimizedDescription += sentence + ".";
    } else {
      break;
    }
  }

  return (
    optimizedDescription || description.substring(0, maxLength - 3) + "..."
  );
};

// Generate keywords from content
export const generateKeywords = (
  content: string,
  maxKeywords: number = 10,
): string[] => {
  const words = content
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 3);

  const wordCount: Record<string, number> = {};
  words.forEach((word) => {
    wordCount[word] = (wordCount[word] || 0) + 1;
  });

  return Object.entries(wordCount)
    .sort(([, a], [, b]) => b - a)
    .slice(0, maxKeywords)
    .map(([word]) => word);
};

// Check SEO score for a page
export const calculateSEOScore = (
  metaTags: MetaTags,
  content: string,
): number => {
  let score = 0;

  // Title optimization
  if (metaTags.title.length >= 30 && metaTags.title.length <= 60) score += 20;
  else if (metaTags.title.length > 0) score += 10;

  // Description optimization
  if (metaTags.description.length >= 120 && metaTags.description.length <= 160)
    score += 20;
  else if (metaTags.description.length > 0) score += 10;

  // Keywords
  if (metaTags.keywords.length > 0) score += 15;

  // Canonical URL
  if (metaTags.canonical) score += 15;

  // Open Graph image
  if (metaTags.ogImage) score += 10;

  // Content length
  if (content.length >= 300) score += 10;

  return Math.min(score, 100);
};

// SEO Analysis Types
export interface SEOAnalysisResult {
  score: number;
  grade: "A" | "B" | "C" | "D" | "F";
  checklist: SEOChecklistItem[];
  keywords: KeywordAnalysis[];
  suggestions: {
    title: string[];
    description: string[];
    keywords: string[];
    content: string[];
  };
  summary: string;
}

export interface SEOChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  status: "pass" | "fail" | "warning";
  priority: "high" | "medium" | "low";
  fix: string;
}

export interface KeywordAnalysis {
  keyword: string;
  density: number;
  frequency: number;
  position: number;
  status: "good" | "warning" | "poor";
  suggestions: string[];
}

// Analyze content for SEO
export const analyzeContent = (
  content: string,
  title: string,
  description: string,
  seoKeywords: string,
): SEOAnalysisResult => {
  const checklist: SEOChecklistItem[] = [];
  const keywordAnalysis: KeywordAnalysis[] = [];

  // Title analysis
  if (title.length < 30) {
    checklist.push({
      id: "title-length",
      category: "title",
      title: "Title too short",
      description: `Title is ${title.length} characters. Should be 30-60 characters.`,
      status: "fail",
      priority: "high",
      fix: "Expand the title to be more descriptive and include target keywords.",
    });
  } else if (title.length > 60) {
    checklist.push({
      id: "title-length",
      category: "title",
      title: "Title too long",
      description: `Title is ${title.length} characters. Should be 30-60 characters.`,
      status: "warning",
      priority: "medium",
      fix: "Shorten the title to fit within 60 characters while maintaining meaning.",
    });
  } else {
    checklist.push({
      id: "title-length",
      category: "title",
      title: "Title length optimal",
      description: `Title is ${title.length} characters, which is optimal.`,
      status: "pass",
      priority: "low",
      fix: "No action needed.",
    });
  }

  // Description analysis
  if (description.length < 120) {
    checklist.push({
      id: "description-length",
      category: "description",
      title: "Description too short",
      description: `Description is ${description.length} characters. Should be 120-160 characters.`,
      status: "fail",
      priority: "high",
      fix: "Expand the description to be more compelling and informative.",
    });
  } else if (description.length > 160) {
    checklist.push({
      id: "description-length",
      category: "description",
      title: "Description too long",
      description: `Description is ${description.length} characters. Should be 120-160 characters.`,
      status: "warning",
      priority: "medium",
      fix: "Shorten the description to fit within 160 characters.",
    });
  } else {
    checklist.push({
      id: "description-length",
      category: "description",
      title: "Description length optimal",
      description: `Description is ${description.length} characters, which is optimal.`,
      status: "pass",
      priority: "low",
      fix: "No action needed.",
    });
  }

  // Content length analysis
  if (content.length < 300) {
    checklist.push({
      id: "content-length",
      category: "content",
      title: "Content too short",
      description: `Content is ${content.length} characters. Should be at least 300 characters.`,
      status: "fail",
      priority: "high",
      fix: "Add more relevant content to provide value to users and search engines.",
    });
  } else if (content.length < 600) {
    checklist.push({
      id: "content-length",
      category: "content",
      title: "Content could be longer",
      description: `Content is ${content.length} characters. Consider adding more content for better SEO.`,
      status: "warning",
      priority: "medium",
      fix: "Add more relevant content to improve user engagement and SEO.",
    });
  } else {
    checklist.push({
      id: "content-length",
      category: "content",
      title: "Content length good",
      description: `Content is ${content.length} characters, which is good for SEO.`,
      status: "pass",
      priority: "low",
      fix: "No action needed.",
    });
  }

  // Keywords analysis
  if (seoKeywords && seoKeywords.length > 0) {
    const keywordList = seoKeywords.split(",").map((k) => k.trim());
    keywordList.forEach((keyword) => {
      const frequency = (
        content.toLowerCase().match(new RegExp(keyword.toLowerCase(), "g")) ||
        []
      ).length;
      const density = (frequency / content.split(" ").length) * 100;

      let status: "good" | "warning" | "poor" = "poor";
      if (density >= 0.5 && density <= 2.5) status = "good";
      else if (density > 0.1 && density < 0.5) status = "warning";

      keywordAnalysis.push({
        keyword,
        density: Math.round(density * 100) / 100,
        frequency,
        position: content.toLowerCase().indexOf(keyword.toLowerCase()),
        status,
        suggestions: generateKeywordSuggestions(keyword),
      });
    });
  } else {
    checklist.push({
      id: "keywords-missing",
      category: "keywords",
      title: "Keywords missing",
      description: "No keywords specified for this content.",
      status: "fail",
      priority: "medium",
      fix: "Add relevant keywords that describe your content and services.",
    });
  }

  // Calculate overall score
  const totalItems = checklist.length;
  const passedItems = checklist.filter((item) => item.status === "pass").length;
  const failedItems = checklist.filter((item) => item.status === "fail").length;
  const warningItems = checklist.filter(
    (item) => item.status === "warning",
  ).length;

  let score = Math.round((passedItems / totalItems) * 100);
  if (failedItems > 0) score = Math.max(0, score - failedItems * 20);
  if (warningItems > 0) score = Math.max(0, score - warningItems * 10);

  // Determine grade
  let grade: "A" | "B" | "C" | "D" | "F";
  if (score >= 90) grade = "A";
  else if (score >= 80) grade = "B";
  else if (score >= 70) grade = "C";
  else if (score >= 60) grade = "D";
  else grade = "F";

  // Generate suggestions
  const suggestions = {
    title: generateTitleSuggestions(title),
    description: generateDescriptionSuggestions(description),
    keywords: generateContentKeywords(content),
    content: generateContentSuggestions(content),
  };

  // Generate summary
  const summary = generateSummary(score, grade, checklist, keywordAnalysis);

  return {
    score,
    grade,
    checklist,
    keywords: keywordAnalysis,
    suggestions,
    summary,
  };
};

// Helper functions
const generateKeywordSuggestions = (keyword: string): string[] => {
  const suggestions = [
    `${keyword} services`,
    `${keyword} company`,
    `${keyword} agency`,
    `${keyword} solutions`,
    `${keyword} experts`,
  ];
  return suggestions.slice(0, 3);
};

const generateTitleSuggestions = (title: string): string[] => {
  const suggestions = [];
  if (title.length < 30) {
    suggestions.push("Add more descriptive words to your title");
    suggestions.push("Include your main keyword in the title");
    suggestions.push("Make the title more compelling");
  } else if (title.length > 60) {
    suggestions.push("Shorten the title while keeping it descriptive");
    suggestions.push("Remove unnecessary words");
    suggestions.push("Focus on the most important information");
  }
  return suggestions;
};

const generateDescriptionSuggestions = (description: string): string[] => {
  const suggestions = [];
  if (description.length < 120) {
    suggestions.push("Expand the description to include more details");
    suggestions.push("Add a call-to-action to the description");
    suggestions.push("Include your main keywords naturally");
  } else if (description.length > 160) {
    suggestions.push("Shorten the description to fit search results");
    suggestions.push("Focus on the most compelling information");
    suggestions.push("Remove redundant phrases");
  }
  return suggestions;
};

const generateContentKeywords = (content: string): string[] => {
  const words = content
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 4);

  const wordCount: Record<string, number> = {};
  words.forEach((word) => {
    wordCount[word] = (wordCount[word] || 0) + 1;
  });

  return Object.entries(wordCount)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([word]) => word);
};

const generateContentSuggestions = (content: string): string[] => {
  const suggestions = [];
  if (content.length < 300) {
    suggestions.push("Add more detailed information about your services");
    suggestions.push("Include customer testimonials or case studies");
    suggestions.push("Add frequently asked questions section");
  }
  if (content.split(" ").length < 100) {
    suggestions.push("Expand on key points with examples");
    suggestions.push("Add industry insights or trends");
    suggestions.push("Include relevant statistics or data");
  }
  return suggestions;
};

const generateSummary = (
  score: number,
  grade: string,
  checklist: SEOChecklistItem[],
  _keywords: KeywordAnalysis[],
): string => {
  const failedItems = checklist.filter((item) => item.status === "fail").length;
  const warningItems = checklist.filter(
    (item) => item.status === "warning",
  ).length;

  if (score >= 90) {
    return `Excellent! Your content has an SEO grade of ${grade} with a score of ${score}/100. Only ${failedItems} critical issues to address.`;
  } else if (score >= 80) {
    return `Good work! Your content has an SEO grade of ${grade} with a score of ${score}/100. Address ${failedItems} critical issues and ${warningItems} warnings to improve.`;
  } else if (score >= 70) {
    return `Your content has an SEO grade of ${grade} with a score of ${score}/100. Focus on fixing ${failedItems} critical issues first, then address ${warningItems} warnings.`;
  } else {
    return `Your content needs improvement with an SEO grade of ${grade} and score of ${score}/100. Prioritize fixing ${failedItems} critical issues to see significant improvement.`;
  }
};

// Export default configuration
const seoService = {
  DEFAULT_SEO,
  PAGE_SEO_CONFIGS,
  generateMetaTags,
  generateStructuredData,
  generateBreadcrumbData,
  generateFAQData,
  generateReviewData,
  optimizeTitle,
  optimizeDescription,
  generateKeywords,
  calculateSEOScore,
  analyzeContent,
};

export default seoService;
